import { AuthCore } from '../core'
import { StorageAdapter, Session, User } from '../types'
import {
  SAMLConfig,
  SAMLAuthResult,
  SAMLUserProfile,
  SAMLRequestState,
  TenantSAMLConfig
} from './types'
import {
  generateSAMLAuthRequest,
  parseSAMLResponse,
  extractUserProfile,
  generateSPMetadata,
  createSAMLRequestState,
  validateSAMLRequestState,
  encodeSAMLRequest
} from './utils'
import { ValidationError, AuthenticationError, NotFoundError } from '../errors'
import { nanoid } from 'nanoid'

/**
 * SAML Authentication Service
 * Handles SAML 2.0 authentication flows
 */
export class SAMLAuthService {
  private auth: AuthCore
  private storage: StorageAdapter
  private samlConfig: Map<string, SAMLConfig> = new Map()
  private requestStates: Map<string, SAMLRequestState> = new Map()

  constructor(auth: AuthCore, storage: StorageAdapter) {
    this.auth = auth
    this.storage = storage
  }

  /**
   * Register SAML configuration for a tenant
   */
  async registerTenantSAMLConfig(tenantId: string, config: SAMLConfig, enabled: boolean = true): Promise<TenantSAMLConfig> {
    if (!config.sp.entityID || !config.sp.acsUrl) {
      throw new ValidationError('SP entityID and acsUrl are required')
    }

    if (!config.idp.entityID || !config.idp.ssoUrl || !config.idp.certificate) {
      throw new ValidationError('IdP entityID, ssoUrl, and certificate are required')
    }

    const tenantConfig: TenantSAMLConfig = {
      ...config,
      tenantId,
      enabled,
      createdAt: new Date(),
      updatedAt: new Date()
    }

    this.samlConfig.set(tenantId, config)

    // Store in database (if table exists)
    await this.storage.storeTenantSAMLConfig?.(tenantConfig)

    return tenantConfig
  }

  /**
   * Get SAML configuration for tenant
   */
  async getTenantSAMLConfig(tenantId: string): Promise<SAMLConfig | null> {
    const cached = this.samlConfig.get(tenantId)
    if (cached) {
      return cached
    }

    // Load from database
    const config = await this.storage.getTenantSAMLConfig?.(tenantId)
    if (config) {
      this.samlConfig.set(tenantId, config)
      return config
    }

    return null
  }

  /**
   * Generate SAML Authentication Request and redirect URL
   */
  async generateAuthRequest(tenantId: string, relayState?: string): Promise<{
    redirectUrl: string
    samlRequest: string
    requestId: string
  }> {
    const config = await this.getTenantSAMLConfig(tenantId)
    if (!config) {
      throw new NotFoundError('SAML configuration', tenantId)
    }

    if (!config.idp.ssoUrl) {
      throw new ValidationError('IdP SSO URL not configured')
    }

    // Generate SAML AuthnRequest
    const { xml, id } = generateSAMLAuthRequest(config)
    const samlRequest = encodeSAMLRequest(xml)

    // Store request state
    const state = createSAMLRequestState(id, relayState)
    this.requestStates.set(id, state)

    // Build redirect URL
    const params = new URLSearchParams({
      SAMLRequest: samlRequest
    })

    if (relayState) {
      params.append('RelayState', relayState)
    }

    const redirectUrl = `${config.idp.ssoUrl}?${params.toString()}`

    return {
      redirectUrl,
      samlRequest,
      requestId: id
    }
  }

  /**
   * Handle SAML Response (Assertion Consumer Service)
   */
  async handleSAMLResponse(
    tenantId: string,
    samlResponse: string,
    relayState?: string
  ): Promise<SAMLAuthResult> {
    try {
      const config = await this.getTenantSAMLConfig(tenantId)
      if (!config) {
        return {
          success: false,
          error: 'SAML configuration not found'
        }
      }

      // Parse and validate SAML response
      const decodedResponse = Buffer.from(samlResponse, 'base64').toString('utf-8')
      const parseResult = parseSAMLResponse(decodedResponse, config)

      if (!parseResult.valid || !parseResult.assertion) {
        return {
          success: false,
          error: parseResult.error || 'Failed to parse SAML response'
        }
      }

      // Validate request state
      const state = this.requestStates.get(parseResult.assertion.inResponseTo)
      if (!state) {
        return {
          success: false,
          error: 'Invalid SAML request state'
        }
      }

      if (new Date(state.expiresAt) < new Date()) {
        return {
          success: false,
          error: 'SAML request state expired'
        }
      }

      // Clean up request state
      this.requestStates.delete(parseResult.assertion.inResponseTo)

      // Extract user profile
      const userProfile = extractUserProfile(parseResult.assertion, config.attributes)

      // Get or create user
      const user = await this.getOrCreateSAMLUser(tenantId, userProfile)

      // Create session
      const session = await this.auth.createSession(user.id)

      return {
        success: true,
        userId: user.id,
        email: user.email,
        profile: userProfile,
        session,
        isNewUser: !user.metadata?.emailVerified
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'SAML authentication failed'
      }
    }
  }

  /**
   * Get or create user from SAML profile
   */
  private async getOrCreateSAMLUser(tenantId: string, profile: SAMLUserProfile): Promise<User> {
    if (!profile.email && !profile.uid) {
      throw new ValidationError('SAML user profile must contain email or uid')
    }

    // Try to find existing user by email
    if (profile.email) {
      const existingUser = await this.storage.getUserByEmail(profile.email)
      if (existingUser) {
        // Link SAML account if not already linked
        if (!existingUser.oauth?.[`saml:${tenantId}`]) {
          await this.storage.linkOAuthAccount(existingUser.id, `saml:${tenantId}`, {
            provider: `saml:${tenantId}`,
            sub: profile.uid,
            email: profile.email,
            name: profile.name || `${profile.firstName || ''} ${profile.lastName || ''}`.trim(),
            linkedAt: new Date()
          })
        }
        return existingUser
      }
    }

    // Try to find existing SAML link
    const linkedUser = await this.storage.getUserByOAuth(`saml:${tenantId}`, profile.uid)
    if (linkedUser) {
      return linkedUser
    }

    // Create new user
    const newUser = await this.storage.createUser({
      email: profile.email,
      name: profile.name || `${profile.firstName || ''} ${profile.lastName || ''}`.trim(),
      plan: 'free',
      entitlements: ['api.read'],
      metadata: {
        emailVerified: !!profile.email,
        samlTenantId: tenantId,
        samlGroups: profile.groups,
        ...profile.attributes
      }
    })

    // Link SAML account
    await this.storage.linkOAuthAccount(newUser.id, `saml:${tenantId}`, {
      provider: `saml:${tenantId}`,
      sub: profile.uid,
      email: profile.email,
      name: profile.name,
      linkedAt: new Date()
    })

    return newUser
  }

  /**
   * Generate SP Metadata
   */
  async generateMetadata(tenantId: string): Promise<string> {
    const config = await this.getTenantSAMLConfig(tenantId)
    if (!config) {
      throw new NotFoundError('SAML configuration', tenantId)
    }

    return generateSPMetadata(config)
  }

  /**
   * Disable SAML for tenant
   */
  async disableSAML(tenantId: string): Promise<void> {
    this.samlConfig.delete(tenantId)

    // Update database
    await this.storage.updateTenantSAMLConfig?.(tenantId, { enabled: false })
  }

  /**
   * Initialize SAML from metadata URL
   */
  async initializeFromMetadata(tenantId: string, metadataUrl: string): Promise<SAMLConfig> {
    try {
      // Fetch and parse IdP metadata
      const response = await fetch(metadataUrl)
      if (!response.ok) {
        throw new Error(`Failed to fetch metadata: ${response.statusText}`)
      }

      const metadata = await response.text()

      // Parse metadata to extract IdP configuration
      const config = this.parseIdPMetadata(metadata)

      // Merge with SP configuration
      const finalConfig: SAMLConfig = {
        sp: {
          entityID: `https://yourdomain.com/saml/${tenantId}`,
          acsUrl: `https://yourdomain.com/saml/acs/${tenantId}`,
          sloUrl: `https://yourdomain.com/saml/slo/${tenantId}`
        },
        idp: config.idp,
        attributes: config.attributes,
        wantAssertionsSigned: true,
        wantResponseSigned: true
      }

      // Register configuration
      await this.registerTenantSAMLConfig(tenantId, finalConfig)

      return finalConfig
    } catch (error) {
      throw new ValidationError(`Failed to initialize SAML from metadata: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Parse IdP Metadata XML
   */
  private parseIdPMetadata(metadata: string): { idp: { entityID: string; ssoUrl: string; sloUrl?: string; certificate: string }; attributes: Record<string, string> } {
    // Simple XML parsing for IdP metadata
    // Extract SingleSignOnService URL
    const ssoMatch = metadata.match(/Location="([^"]*)".*?Binding="urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect"/s)
    const ssoUrl = ssoMatch?.[1] || metadata.match(/Location="([^"]*)"/)?.[1] || ''

    // Extract certificate
    const certMatch = metadata.match(/<X509Certificate>(.*?)<\/X509Certificate>/s)
    const certificate = certMatch?.[1] || ''

    // Extract entity ID
    const entityMatch = metadata.match(/entityID="([^"]*)"/s)
    const entityID = entityMatch?.[1] || 'unknown'

    return {
      idp: {
        entityID,
        ssoUrl,
        certificate
      },
      attributes: {
        email: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
        firstName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname',
        lastName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname'
      }
    }
  }

  /**
   * Create Single Logout Request
   */
  async generateLogoutRequest(tenantId: string, sessionIndex?: string): Promise<{
    redirectUrl: string
    samlRequest: string
  }> {
    const config = await this.getTenantSAMLConfig(tenantId)
    if (!config || !config.idp.sloUrl) {
      throw new ValidationError('SAML logout not configured')
    }

    // Create LogoutRequest XML
    const logoutRequestId = `_${nanoid()}`
    const issueInstant = new Date().toISOString()

    const logoutRequest = `<?xml version="1.0" encoding="UTF-8"?>
<samlp:LogoutRequest xmlns:samlp="urn:oasis:names:tc:SAML:2.0:protocol"
                     xmlns:saml="urn:oasis:names:tc:SAML:2.0:assertion"
                     ID="${logoutRequestId}"
                     Version="2.0"
                     IssueInstant="${issueInstant}"
                     Destination="${config.idp.sloUrl}">
  <saml:Issuer Format="urn:oasis:names:tc:SAML:2.0:nameid-format:entity">${config.sp.entityID}</saml:Issuer>
  ${sessionIndex ? `<samlp:SessionIndex>${sessionIndex}</samlp:SessionIndex>` : ''}
</samlp:LogoutRequest>`

    const samlRequest = Buffer.from(logoutRequest).toString('base64')

    const params = new URLSearchParams({
      SAMLRequest: samlRequest
    })

    const redirectUrl = `${config.idp.sloUrl}?${params.toString()}`

    return {
      redirectUrl,
      samlRequest
    }
  }
}
